import React from "react";
import Avatar from "../common/Avatar";
import useTranslate from "../../hooks/useTranslate";
import shortRelativeTime from "./relativeTime";

// Agent Lounge — Activity panel body (QAL-T8-FE). Design source: Agent Lounge
// .dc.html frame 1o (desktop popover 380px + mobile bottom-sheet) and 1p·3
// (empty "all caught up"). Shared inner content for BOTH the desktop popover
// and the mobile sheet in LoungeHeader — the wrapper (popover vs sheet chrome)
// lives there; this renders header row + list/empty/loading/error.
//
// Contract (§2): items are reply/mention only — NO vote milestones (GATE0 §2,
// the "passed 15 upvotes" row drawn in 1o is deliberately NOT built). Unread
// row = bg #F8FBFF + blue dot; read row = white, greyed text, "· read".
// text_excerpt is the actor's new comment text; the connective phrase is an
// i18n key, the name is a proper noun (not translated).

// Relative timestamp for a row, e.g. "1h ago" / "now"; read rows append
// "· read" (design 1o "Yesterday · read").
const ActivityTime = ({ createdAt, read }) => {
  const { t } = useTranslate();
  const rel = shortRelativeTime(createdAt);
  const when = rel === "now" ? t("now") : `${rel} ${t("ago")}`;
  return (
    <p className="m-0 mt-0.5 text-[12px] text-[#9CA3AF]">
      {read ? `${when} · ${t("read")}` : when}
    </p>
  );
};

const ActivityRow = ({ item, onClick }) => {
  const { t } = useTranslate();
  const read = Boolean(item.read_at);
  const actor = item.actor || {};
  const name = actor.name || "";
  const phrase =
    item.type === "mention"
      ? t("mentioned you in a comment")
      : t("replied to you");
  const excerpt = (item.text_excerpt || "").trim();

  return (
    <button
      type="button"
      onClick={() => onClick && onClick(item)}
      data-testid="lounge-activity-item"
      className={`w-full text-left flex gap-2.5 px-4 py-3 border-b border-[#EEF1F5] last:border-b-0 ${
        read ? "bg-white hover:bg-[#F8FAFC]" : "bg-[#F8FBFF]"
      }`}
    >
      <span className="shrink-0">
        <Avatar
          src={actor.profile_picture_url}
          alt={name}
          size={30}
          fallbackText={name}
          initialsSize={11}
        />
      </span>
      <span className="flex-1 min-w-0">
        <span
          className={`block text-[13px] leading-[1.5] ${
            read ? "text-[#6B7280]" : "text-[#111827]"
          }`}
        >
          <span className="font-semibold text-[#111827]" data-no-translate="true">
            {name}
          </span>{" "}
          {phrase}
          {excerpt ? (
            <>
              {": "}
              <span data-no-translate="true">&ldquo;{excerpt}&rdquo;</span>
            </>
          ) : null}
        </span>
        <ActivityTime createdAt={item.created_at} read={read} />
      </span>
      {!read && (
        <span
          className="w-2 h-2 rounded-full bg-[#2283FF] shrink-0 mt-[5px]"
          aria-hidden="true"
        />
      )}
    </button>
  );
};

const LoungeActivityPanel = ({
  items = [],
  loading = false,
  error = false,
  unreadCount = 0,
  onMarkAllRead,
  onItemClick,
  variant = "popover",
}) => {
  const { t } = useTranslate();
  const isSheet = variant === "sheet";
  const isEmpty = !loading && !error && items.length === 0;

  return (
    <div className={isSheet ? "flex flex-col min-h-0" : ""}>
      {/* Header row — title + "Mark all read" (only when something is unread). */}
      <div className="flex items-center px-4 py-[13px] border-b border-[#EEF1F5] shrink-0">
        <span
          className={`font-bold text-[#111827] ${
            isSheet ? "text-[15px]" : "text-[14px]"
          }`}
        >
          {t("Activity")}
        </span>
        <span className="flex-1" />
        {unreadCount > 0 && (
          <button
            type="button"
            onClick={onMarkAllRead}
            className="border-none bg-none text-[#2283FF] text-[12.5px] font-semibold p-0"
          >
            {t("Mark all read")}
          </button>
        )}
      </div>

      {/* Body */}
      <div
        className={
          isSheet
            ? "overflow-y-auto flex-1 min-h-0"
            : "max-h-[min(70vh,440px)] overflow-y-auto"
        }
      >
        {loading ? (
          <div className="px-4 py-3 flex flex-col gap-3" data-testid="lounge-activity-loading">
            {[0, 1, 2].map((i) => (
              <div key={i} className="flex gap-2.5 animate-pulse">
                <span className="w-[30px] h-[30px] rounded-full bg-[#EEF1F5] shrink-0" />
                <span className="flex-1 min-w-0">
                  <span className="block h-3 w-3/4 rounded bg-[#EEF1F5]" />
                  <span className="block h-2.5 w-1/4 rounded bg-[#F1F5FA] mt-2" />
                </span>
              </div>
            ))}
          </div>
        ) : error ? (
          <div className="px-5 py-7 flex flex-col items-center text-center">
            <p className="m-0 text-[13px] text-[#6B7280]">
              {t("Couldn't load activity. Please try again.")}
            </p>
          </div>
        ) : isEmpty ? (
          // 1p·3 — all caught up (same optics as the T3 empty state).
          <div className="py-7 px-5 flex flex-col items-center text-center">
            <span className="w-11 h-11 rounded-full bg-[#F1F5FA] flex items-center justify-center">
              <svg width="19" height="19" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                <path
                  d="M3.5 8.5l3 3 6-6.5"
                  stroke="#9CA3AF"
                  strokeWidth="1.6"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </span>
            <p className="m-0 mt-3 text-[13.5px] font-semibold text-[#111827]">
              {t("You're all caught up.")}
            </p>
            <p className="m-0 mt-1 text-[12.5px] text-[#6B7280]">
              {t("Replies and mentions will show up here.")}
            </p>
          </div>
        ) : (
          items.map((item) => (
            <ActivityRow key={item.id} item={item} onClick={onItemClick} />
          ))
        )}
      </div>
    </div>
  );
};

export default LoungeActivityPanel;
